import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { FilterService } from '../../../core/services/filter.service';
import { FilterActions } from './filter.actions';
import {
  IFilterListFilter,
  IFilterListResponse,
  IFilterListSort,
  IFilterSingleResponse,
} from '../../models/filter.model';
import { Router } from '@angular/router';
import { Store, Action } from '@ngrx/store';
import { getFilterQueries, selectFilter } from './filter.selectors';
import { AlertService } from 'src/app/core/services/alert.service';

// Helper function to handle API calls
function handleApiCall<T extends { message?: string }>(
  apiCall: Promise<T>,
  successAction: any,
  failureAction: any,
  alertService?: AlertService
) {
  return from(apiCall).pipe(
    map((response) => {
      if (alertService && response.message) {
        alertService.success(
          response.message,
          'Success',
        );
      }
      return successAction(response);
    }),
    catchError((error) => {
      return of(failureAction({ error: error.message }));
    })
  );
}

@Injectable()
export class FilterEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly store: Store,
    private readonly filterService: FilterService,
    private readonly alertService: AlertService
  ) { }

  loadFiltersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.loadFiltersList),
      withLatestFrom(this.store.select(getFilterQueries)),
      mergeMap(([, filters]) => {
        const filter: IFilterListFilter = filters.body?.filter ?? {};
        const limit = 10;
        const sort: IFilterListSort = filters.body?.sort ?? {
          key: 'createdAt',
          order: 'desc',
        };
        return handleApiCall(
          this.filterService.getFilters(
            filters.params?.page,
            limit,
            filter,
            sort
          ),
          (customfilters: IFilterListResponse) =>
            FilterActions.loadFiltersListSuccess({
              response: customfilters,
              filterData: {
                params: filters.params,
                body: { filter, sort },
              },
            }),
          FilterActions.loadFiltersListFailure
        );
      })
    )
  );

  // For loading single filter
  loadSingleFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.loadSingleFilter),
      mergeMap(({ id }) =>
        handleApiCall(
          this.filterService.getFilter(id),
          (response: IFilterSingleResponse) =>
            FilterActions.loadSingleFilterSuccess({ filter: response.data }),
          FilterActions.loadSingleFilterFailure
        )
      )
    )
  );

  // For select filter
  selectFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.selectFilter),
      mergeMap(({ id, withRedirect = false }) =>
        handleApiCall(
          this.filterService.getFilter(id),
          (response: IFilterSingleResponse) =>
            FilterActions.selectFilterSuccess({
              filter: response.data,
              withRedirect,
            }),
          FilterActions.selectFilterFailure
        )
      )
    )
  );
  loadFiltersNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.loadFiltersNames),
      mergeMap(() =>
        this.filterService.listFilterName().pipe(
          map((filterResponse) => {
                      console.log('API Response:', filterResponse);

              return FilterActions.loadFilterNamesSuccess({
            response: filterResponse
          });
        }),
          catchError((error) =>
            of(
              FilterActions.loadFilterNamesFailure({ error: error.message })
            )
          )
        )
      )
    )
  );


  redirectAfterSelectFilter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.selectFilterSuccess),
        tap(({ withRedirect }) => {
          if (withRedirect) {
            this.router.navigate(['/client']);
          }
        })
      ),
    { dispatch: false }
  );

  createFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.createFilter),
      mergeMap((action) =>
        from(this.filterService.createFilter(action.filter)).pipe(
          map((res: IFilterSingleResponse) => {
            this.alertService.success(
              res.message,
              'Success',
              `${res.data.filter_name} `
            );
            action.closeModalSignal.set(true);
            return FilterActions.createFilterSuccess({ filter: res.data });
          }),
          catchError((error) =>
            of(FilterActions.createFilterFailure({ error: error.message }))
          )
        )
      )
    )
  );

  updateFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.updateFilter),
      mergeMap((action) =>
        from(this.filterService.updateFilter(action.id, action.filter)).pipe(
          map((res: IFilterSingleResponse) => {
            this.alertService.success(
              res.message,
              'Success',
              `${res.data.filter_name} `
            );
            action.closeModalSignal.set(true);
            return FilterActions.updateFilterSuccess({ filter: res.data });
          }),
          catchError((error) =>
            of(FilterActions.updateFilterFailure({ error: error.message }))
          )
        )
      )
    )
  );

  redirectAfterCreateUpdate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          FilterActions.createFilterSuccess,
          FilterActions.updateFilterSuccess,
          FilterActions.deleteFilterSuccess,
          FilterActions.changeFilterStatusSuccess // on loadlist -> selectuser will set null hence removed: temp- TBD
        ),
        withLatestFrom(
          this.store.select(getFilterQueries),
          this.store.select(selectFilter) // Read one more value from selectFilter
        ),
        mergeMap(([action, , selectedFilter]) => {
          const filterId = action.type === FilterActions.changeFilterStatusSuccess.type ? action.id : selectedFilter?._id ?? '';
          const actionsToDispatch: Array<Action> = [
            FilterActions.clearCurrentFilter(),
            FilterActions.loadFiltersList(),
          ];
          if (action.type === FilterActions.changeFilterStatusSuccess.type) {
            actionsToDispatch.push(FilterActions.selectFilter({ id: filterId }))
          }
          if (action.type === FilterActions.deleteFilterSuccess.type) {
            actionsToDispatch.push(FilterActions.clearSelectedFilter())
          }
          return actionsToDispatch;
        })
      )
  );

deleteFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.deleteFilter),
      mergeMap((action) =>
        this.filterService.deleteFilter(action.id).pipe(
          map((response) => {
            action.closeModalSignal.set(true); // Trigger the modal close
            this.alertService.success(response?.message, 'Success');
            return FilterActions.deleteFilterSuccess({
              id: action.id,
              message: response.message,
            });
          }),
          catchError((error) => {
            return of(
              FilterActions.deleteFilterFailure({ error: error.message })
            );
          })
        )
      )
    )
  );
  // Redirect after delete filter from filter page
  redirectAfterDeleteFilter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.deleteFilterSuccess),
        tap(() => {
          this.router.navigate(['/custom-data-filters']);
        })
      ),
    { dispatch: false }
  );

  changeFilterStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.changeFilterStatus),
      mergeMap((action) =>
        handleApiCall(
          this.filterService.changeFilterStatus(action.id),
          () => FilterActions.changeFilterStatusSuccess({ id: action.id, fetchClients: action.fetchClients }),
          FilterActions.changeFilterStatusFailure,
          this.alertService
        )
      )
    )
  );
}
